<template>
  <div class="screen" @click="revealAll">
    <!-- The headline gives the winner away, so it waits for the last row. -->
    <h2>{{ finished ? headline : 'Counting up…' }}</h2>

    <div class="scores">
      <div
        v-for="entry in revealed"
        :key="entry.id"
        class="score-row score-row--enter"
        :class="{ 'score-row--winner': entry.isWinner && finished }"
      >
        <span class="score-row__rank">{{ entry.rank }}</span>
        <span class="score-row__name">{{ entry.name }}</span>
        <span class="score-row__total">{{ entry.score }}</span>
        <span class="score-row__detail">{{ workingFor(entry) }}</span>
      </div>
    </div>

    <p v-if="!finished" class="counter-band">Tap to skip</p>

    <template v-else>
      <button v-if="midMatch" class="primary" @click.stop="nextRound">
        Deal round {{ match.round + 1 }} of {{ match.rounds }}
      </button>
      <!-- Online: the same table again, same room and code (rematchState). -->
      <button v-else-if="isOnline" class="primary" @click.stop="rematch">Rematch</button>
      <button v-else class="primary" @click.stop="playAgain">Play again</button>
      <button class="secondary" @click.stop="backToMenu">Back to menu</button>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

// Bug report (2026-08-27): "It would be nice if the scoreboard also had a
// little bit of drama as it revealed the scores."
//
// Worst first, best last, one at a time - the order a room reads results out
// in. The winner is the last thing to land and the headline is withheld until
// then, because printing "You win" above an empty board gives away the only
// thing worth waiting for.
//
// A whole table's scores are all decided the moment the last card is taken;
// this is presentation, and it must never be in anyone's way. Hence: a tap
// anywhere finishes it instantly, the buttons only appear once it's done (so
// nobody can mis-tap "Play again" reaching for "skip"), and a reduced-motion
// preference gets the whole board at once.
const REVEAL_STEP_MS = 700;

export default {
  name: 'ScoreSheet',
  emits: ['menu'],
  data() {
    return { shown: 0, timer: null };
  },
  computed: {
    ...mapGetters(['scores', 'match', 'isOnline']),
    // A multi-round match (house rule - constants.js, MAX_ROUNDS). Rows are
    // then ranked by running total, and each also carries its round score.
    multiRound() { return Boolean(this.match && this.match.rounds > 1); },
    midMatch() { return this.multiRound && !this.match.over; },
    /**
     * The rows on screen: the worst `shown` places, still in rank order.
     *
     * Taking from the END is what makes this work. The reveal wants to build
     * up to the winner, but the finished board has to read 1-2-3-4 downwards
     * like every scoreboard - so instead of appending worst-first (which
     * leaves the board upside down when it stops), each new row is a lower
     * rank inserted at the TOP, and the winner is the last thing to land
     * there. Right at every intermediate step, and right at the end.
     */
    revealed() { return this.scores.slice(this.scores.length - this.shown); },
    finished() { return this.shown >= this.scores.length; },
    winners() { return this.scores.filter((e) => e.isWinner); },
    headline() {
      const human = this.scores.find((e) => e.id === 'you');
      if (this.midMatch) {
        // Nobody has won anything yet - the low total is only a lead.
        const after = `after round ${this.match.round} of ${this.match.rounds}`;
        if (this.winners.length > 1) {
          return `${this.winners.map((w) => w.name).join(' and ')} share the lead ${after}`;
        }
        const leader = this.winners[0];
        if (leader && human && leader.id === human.id) return `You lead ${after}`;
        return `${leader?.name} leads ${after}`;
      }
      if (this.winners.length > 1) {
        // The rulebook prints no tie-break, so a tie is a real shared result
        // and gets said out loud rather than resolved arbitrarily.
        const names = this.winners.map((w) => w.name).join(' and ');
        return `${names} tie for the win`;
      }
      const winner = this.winners[0];
      if (winner && human && winner.id === human.id) return 'You win';
      return `${winner?.name} wins`;
    },
  },
  mounted() {
    // Somebody who has asked for less motion gets the finished board, not a
    // slower version of the animation.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (still) { this.shown = this.scores.length; return; }
    this.step();
  },
  beforeUnmount() {
    if (this.timer) window.clearTimeout(this.timer);
  },
  methods: {
    step() {
      if (this.finished) return;
      this.shown += 1;
      this.timer = window.setTimeout(() => { this.timer = null; this.step(); }, REVEAL_STEP_MS);
    },
    revealAll() {
      if (this.finished) return;
      if (this.timer) { window.clearTimeout(this.timer); this.timer = null; }
      this.shown = this.scores.length;
    },
    /**
     * Shows the scoring arithmetic, because the series rule is the one thing
     * nobody can verify by eye: "13 + 15 − 8 counters". Each run contributes
     * only its lowest card, and printing exactly those numbers is what makes
     * a surprising score legible instead of suspicious.
     */
    workingFor(entry) {
      let working;
      if (entry.runs.length === 0) {
        working = `no cards − ${entry.counters} counters`;
      } else {
        const terms = entry.runs.map((run) => (run.length > 1
          ? `${run[0]} (of ${run[0]}–${run[run.length - 1]})`
          : `${run[0]}`));
        working = `${terms.join(' + ')} − ${entry.counters} counters`;
      }
      // In a match the big number is the running total, so the round's own
      // score is spelled out in front of the arithmetic that produced it.
      if (this.multiRound) return `round ${this.match.round}: ${entry.roundScore} = ${working}`;
      return working;
    },
    nextRound() {
      this.$store.dispatch('nextRound');
    },
    // Online, Rematch deals again in the same room - it never starts a solo
    // game while bound to it. That used to happen from Play again: the table
    // went on believing it was online, looked for a seat with my uid in a
    // bots-only game, and crashed (Sentry, 2026-10-09).
    rematch() {
      this.$store.dispatch('rematch');
    },
    playAgain() {
      this.$store.dispatch('newGame');
    },
    async backToMenu() {
      await this.$store.dispatch(this.isOnline ? 'leaveRoom' : 'abandonGame');
      this.$emit('menu');
    },
  },
};
</script>
